'use client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@aqarmap/aqarmap-ui';
import Estimations from '~valuation/pages/MyValuations/Estimations';
import Portfolios from '~valuation/pages/MyValuations/Portfolios';
import TabButton from '~valuation/pages/MyValuations/TabButton';
import { useFullRoutes, useQuery } from '~/hooks';
import { ValuationTypeEnum } from '~/constants/enums/valuationEnums';
import { useGetValuationStatsQuery } from '~/redux/api/valuationApiSlice';
import '~valuation/styles.css';

type TabKeyType = ValuationTypeEnum;

export default function MyValuations() {
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const { VALUATION_URL } = useFullRoutes();
  const query = useQuery();
  const valuationActiveType =
    query['valuation-type'] || ValuationTypeEnum.PORTFOLIO;

  const [activeTab, setActiveTab] = useState<TabKeyType>(
    valuationActiveType.toUpperCase() as TabKeyType,
  );
  const { data: statsData } = useGetValuationStatsQuery({
    type: ValuationTypeEnum.PORTFOLIO,
  });

  const portfoliosCount =
    statsData?.typeCounts?.[ValuationTypeEnum.PORTFOLIO] ?? 0;
  const estimationsCount =
    statsData?.typeCounts?.[ValuationTypeEnum.ESTIMATION] ?? 0;
  const showAddNewButton =
    activeTab === ValuationTypeEnum.PORTFOLIO
      ? portfoliosCount > 0
      : estimationsCount > 0;

  return (
    <div className="layout-boundary pt-4x pb-10x">
      <div className="layout-boundary__inner flex flex-col px-2x md:px-0">
        <div className="flex items-center justify-between mb-4x">
          <p className="text-title_3 text-black">{t('propertyValuation')}</p>
          {showAddNewButton && (
            <Button
              size="medium"
              variant="primary"
              Icon={<Icon name="plus" size="24px" />}
              className="px-x py-x md:px-3x md:py-1.5x "
              onClick={() => navigate(VALUATION_URL.ADD)}
            >
              <span className="hidden md:inline">{t('addNewValuation')}</span>
            </Button>
          )}
        </div>

        <div className="flex w-full  rounded-[40px] border border-gray overflow-hidden mb-3x md:mb-4x">
          <TabButton
            isCountVisible={activeTab !== ValuationTypeEnum.PORTFOLIO}
            onTabClick={() => setActiveTab(ValuationTypeEnum.PORTFOLIO)}
            label={t('portfolio')}
            icon={<Icon name="folder" />}
            active={activeTab === ValuationTypeEnum.PORTFOLIO}
            count={portfoliosCount}
          />
          <TabButton
            isCountVisible={activeTab !== ValuationTypeEnum.ESTIMATION}
            onTabClick={() => setActiveTab(ValuationTypeEnum.ESTIMATION)}
            label={t('valuations')}
            icon={<Icon name="evaluate-property" />}
            active={activeTab === ValuationTypeEnum.ESTIMATION}
            count={estimationsCount}
          />
        </div>

        <div
          className={
            activeTab === ValuationTypeEnum.ESTIMATION ? 'block' : 'hidden'
          }
        >
          <Estimations />
        </div>
        <div
          className={
            activeTab === ValuationTypeEnum.PORTFOLIO ? 'block' : 'hidden'
          }
        >
          <Portfolios />
        </div>
      </div>
    </div>
  );
}
